// /customers — 客户列表
// v1.9 P0: 移动端表格退化为 card list
// v1.27.2 fix: 加 <Outlet /> 渲染 /customers/$customerId 和 /customers/new 子路由
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Link,
  Outlet,
  createFileRoute,
  useLocation,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { BgOpsButton } from '../components/BgOpsButton';
import { EmptyState } from '../components/EmptyState';
import { Icon } from '../components/Icon';
import { CardListSkeleton, TableSkeleton } from '../components/Skeleton';
import { Toast } from '../components/Toast';
import { type Customer, customerApi } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';

export const Route = createFileRoute('/customers')({
  component: CustomersPage,
  validateSearch: (search: Record<string, unknown>): { stage?: string } => ({
    stage: typeof search.stage === 'string' ? search.stage : undefined,
  }),
});

const STAGE_LABELS: Record<Customer['stage'], string> = {
  prospect: '潜在',
  contacted: '已联系',
  quoted: '已报价',
  sampled: '已样品',
  ordered: '已大货',
  delivered: '已交付',
  lost: '流失',
};

const STAGE_COLORS: Record<Customer['stage'], string> = {
  prospect: 'tag',
  contacted: 'tag tag-stage-contacted',
  quoted: 'tag tag-stage-quoted',
  sampled: 'tag tag-stage-sampled',
  ordered: 'tag tag-good',
  delivered: 'tag tag-good',
  lost: 'tag',
};

const COUNTRY_FLAGS: Record<string, string> = {
  JP: '🇯🇵',
  US: '🇺🇸',
  GB: '🇬🇧',
  CN: '🇨🇳',
  DE: '🇩🇪',
  FR: '🇫🇷',
  KR: '🇰🇷',
};

const SOURCE_LABELS: Record<string, string> = {
  linkedin: 'LinkedIn',
  jimoto: '地推',
  exhibition: '展会',
  referral: '转介绍',
  web3forms: '官网表单',
  weekly_prospect: '周拓客',
  weekly_prospect_auto: '自动拓客',
  mcp_bulk_import: 'MCP 批量',
  manual: '手动',
};

const SORT_OPTIONS: Array<{
  value: 'createdAt' | 'updatedAt' | 'name' | 'recentActivity';
  label: string;
  order: 'asc' | 'desc';
}> = [
  { value: 'createdAt', label: '加入日期', order: 'desc' },
  { value: 'updatedAt', label: '最近更新', order: 'desc' },
  { value: 'recentActivity', label: '最近互动', order: 'desc' },
  { value: 'name', label: '公司名', order: 'asc' },
];

const NEW_THRESHOLD_DAYS = 7;
const ACTIVE_THRESHOLD_DAYS = 7;

/** 判断客户是否"新"（创建于 NEW_THRESHOLD_DAYS 天内） */
function isNewCustomer(createdAt: number): boolean {
  return Date.now() - createdAt < NEW_THRESHOLD_DAYS * 24 * 60 * 60 * 1000;
}

/** 判断客户是否"活跃"（updatedAt 在 ACTIVE_THRESHOLD_DAYS 天内, 排除已交付/流失的） */
function isActiveCustomer(c: Customer): boolean {
  if (c.stage === 'delivered' || c.stage === 'lost') return false;
  return Date.now() - c.updatedAt < ACTIVE_THRESHOLD_DAYS * 24 * 60 * 60 * 1000;
}

function CustomersPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentCompanyId } = useAuthStore();

  const [search, setSearch] = useState('');
  const searchParams = useSearch({ from: '/customers' });
  const [stage, setStage] = useState<string>(() => searchParams.stage ?? '');
  const [sortBy, setSortBy] = useState<{
    value: 'createdAt' | 'updatedAt' | 'name' | 'recentActivity';
    label: string;
    order: 'asc' | 'desc';
  }>(SORT_OPTIONS[0]!);
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);

  // v1.27.2 fix: 详情/新建时只渲染 Outlet, 不渲染 list (hooks 必须无条件调用)
  const location = useLocation();
  const onChild =
    location.pathname !== '/customers' &&
    (location.pathname.startsWith('/customers/') || location.pathname === '/customers/new');

  const { data, isLoading, error } = useQuery({
    queryKey: ['customers', currentCompanyId, { search, stage, sortBy: sortBy.value }],
    queryFn: () =>
      customerApi.list({
        search: search || undefined,
        stage: stage || undefined,
        sort: sortBy.value,
        order: sortBy.order,
        limit: 100,
      }),
    enabled: !onChild,
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => customerApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setToast({ kind: 'success', msg: '已删除客户' });
    },
    onError: (e) => {
      setToast({ kind: 'error', msg: (e as Error).message });
    },
  });

  function handleDelete(c: Customer) {
    if (!window.confirm(`确定删除客户「${c.name}」吗？`)) return;
    removeMutation.mutate(c.id);
  }

  if (onChild) return <Outlet />;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4 sm:mb-5">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold mb-1">客户档案</h1>
          <p className="text-ink-3 text-xs sm:text-sm">共 {data?.total ?? 0} 条 · 当前公司视角</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <BgOpsButton />
          <button
            type="button"
            onClick={() => navigate({ to: '/customers/new' })}
            className="w-full sm:w-auto px-4 py-2 bg-accent text-white rounded text-sm font-medium hover:bg-accent-2 transition-colors min-h-[40px]"
          >
            + 新建客户
          </button>
        </div>
      </div>

      {/* 过滤 */}
      <div className="bg-surface border border-line rounded p-3 mb-4 flex flex-col sm:flex-row gap-2">
        <div className="flex-1 relative">
          <Icon
            name="search"
            size="sm"
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-3 pointer-events-none"
          />
          <input
            type="search"
            placeholder="搜索公司名 / 标签 / 备注"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-2 border border-line rounded text-sm bg-surface"
            aria-label="搜索客户"
          />
        </div>
        <select
          value={stage}
          onChange={(e) => setStage(e.target.value)}
          className="px-3 py-2 border border-line rounded text-sm bg-surface"
        >
          <option value="">全部阶段</option>
          {Object.entries(STAGE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <select
          value={sortBy.value}
          onChange={(e) => {
            const found = SORT_OPTIONS.find((o) => o.value === e.target.value);
            if (found) setSortBy(found);
          }}
          className="px-3 py-2 border border-line rounded text-sm bg-surface"
          aria-label="排序方式"
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              排序: {o.label}
            </option>
          ))}
        </select>
      </div>

      {/* 列表 */}
      <div className="bg-surface border border-line rounded overflow-hidden">
        {isLoading ? (
          <>
            <div className="hidden md:block">
              <TableSkeleton rows={5} />
            </div>
            <div className="md:hidden p-2">
              <CardListSkeleton rows={5} />
            </div>
          </>
        ) : error ? (
          <div className="p-8 text-center text-bad text-sm">
            加载失败：{(error as Error).message}
          </div>
        ) : data?.items.length === 0 ? (
          <EmptyState
            icon="users"
            title="还没有客户"
            description="从「找客」页用 Dork 串搜，或手动添加第一个客户"
            action={
              <button
                type="button"
                onClick={() => navigate({ to: '/customers/new' })}
                className="px-3 py-1.5 bg-accent text-white rounded text-sm hover:bg-accent-2 transition-colors"
              >
                + 新建客户
              </button>
            }
          />
        ) : (
          <>
            {/* 桌面: 表格 (≥ md) */}
            <table className="hidden md:table w-full">
              <thead>
                <tr className="bg-surface-2 text-ink-2 text-xs">
                  <th className="text-left px-4 py-2 font-medium">客户</th>
                  <th className="text-left px-4 py-2 font-medium">国家</th>
                  <th className="text-left px-4 py-2 font-medium">来源</th>
                  <th className="text-left px-4 py-2 font-medium">阶段</th>
                  {/* v1.9.1: 背调信号列 */}
                  <th className="text-left px-4 py-2 font-medium">背调</th>
                  <th className="text-left px-4 py-2 font-medium">偏好</th>
                  <th className="text-left px-4 py-2 font-medium">最后互动</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {data?.items.map((c) => {
                  // v1.9.1: 计算 traffic light (基于 redFlags)
                  const high = (c.redFlags ?? []).filter((f) => f.severity === 'high').length;
                  const medium = (c.redFlags ?? []).filter((f) => f.severity === 'medium').length;
                  const trafficLight =
                    high > 0 ? '🔴' : medium >= 2 ? '🔴' : medium >= 1 ? '🟡' : '🟢';
                  const bgStatus = c.backgroundCheckStatus ?? 'pending';
                  const isNew = isNewCustomer(c.createdAt);
                  const isActive = isActiveCustomer(c);
                  return (
                    <tr key={c.id} className="border-t border-line hover:bg-surface-2">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <span
                            className="text-base shrink-0"
                            title={`背调: ${bgStatus} · 红旗: 🔴${high} 🟡${medium}`}
                          >
                            {trafficLight}
                          </span>
                          <Link
                            to="/customers/$customerId"
                            params={{ customerId: c.id }}
                            className="block min-w-0 flex-1"
                          >
                            <div className="flex items-center gap-1.5">
                              <div className="font-medium text-sm truncate">{c.name}</div>
                              {isNew && (
                                <span
                                  className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold rounded bg-good/15 text-good border border-good/30 shrink-0"
                                  title={`加入 ${Math.floor((Date.now() - c.createdAt) / (24 * 60 * 60 * 1000))} 天前`}
                                >
                                  ✨ New
                                </span>
                              )}
                              {isActive && (
                                <span
                                  className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold rounded bg-info/15 text-info border border-info/30 shrink-0"
                                  title="最近 7 天有互动"
                                >
                                  🔥 Active
                                </span>
                              )}
                            </div>
                            {c.city && <div className="text-xs text-ink-3">{c.city}</div>}
                          </Link>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center">
                          {COUNTRY_FLAGS[c.country] ?? (
                            <Icon name="building" size="sm" className="text-ink-3" />
                          )}
                        </span>{' '}
                        {c.country}
                      </td>
                      <td className="px-4 py-3 text-xs text-ink-2">{c.source}</td>
                      <td className="px-4 py-3">
                        <span className={STAGE_COLORS[c.stage]}>{STAGE_LABELS[c.stage]}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-ink-2">
                        {c.incotermPref && <span>{c.incotermPref}</span>}
                        {c.paymentPref && <span className="ml-1">· {c.paymentPref}</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-ink-3">
                        {new Date(c.updatedAt).toLocaleDateString('zh-CN')}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => handleDelete(c)}
                          className="text-xs text-ink-3 hover:text-bad"
                        >
                          删除
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* 移动: 卡片列表 (< md) */}
            <ul className="md:hidden p-2 space-y-2">
              {data?.items.map((c) => {
                const isNew = isNewCustomer(c.createdAt);
                const isActive = isActiveCustomer(c);
                return (
                  <li
                    key={c.id}
                    className="bg-surface-2 border border-line rounded-lg p-3 animate-fade-up"
                  >
                    <Link
                      to="/customers/$customerId"
                      params={{ customerId: c.id }}
                      className="block"
                    >
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <div className="font-medium text-sm text-ink truncate">{c.name}</div>
                            {isNew && (
                              <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold rounded bg-good/15 text-good border border-good/30 shrink-0">
                                ✨ New
                              </span>
                            )}
                            {isActive && (
                              <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold rounded bg-info/15 text-info border border-info/30 shrink-0">
                                🔥 Active
                              </span>
                            )}
                          </div>
                          {c.city && <div className="text-xs text-ink-3 truncate">{c.city}</div>}
                        </div>
                        <span className={`${STAGE_COLORS[c.stage]} shrink-0`}>
                          {STAGE_LABELS[c.stage]}
                        </span>
                      </div>
                      <dl className="text-xs space-y-0.5 mt-2">
                        <div className="flex gap-2">
                          <dt className="text-ink-3 w-14 shrink-0">国家</dt>
                          <dd className="text-ink-2">
                            <span className="inline-flex items-center">
                              {COUNTRY_FLAGS[c.country] ?? (
                                <Icon name="building" size="sm" className="text-ink-3" />
                              )}
                            </span>{' '}
                            {c.country}
                          </dd>
                        </div>
                        <div className="flex gap-2">
                          <dt className="text-ink-3 w-14 shrink-0">来源</dt>
                          <dd className="text-ink-2 truncate">{c.source}</dd>
                        </div>
                        {(c.incotermPref || c.paymentPref) && (
                          <div className="flex gap-2">
                            <dt className="text-ink-3 w-14 shrink-0">偏好</dt>
                            <dd className="text-ink-2 truncate">
                              {[c.incotermPref, c.paymentPref].filter(Boolean).join(' · ')}
                            </dd>
                          </div>
                        )}
                        <div className="flex gap-2">
                          <dt className="text-ink-3 w-14 shrink-0">更新</dt>
                          <dd className="text-ink-3">
                            {new Date(c.updatedAt).toLocaleDateString('zh-CN')}
                          </dd>
                        </div>
                      </dl>
                    </Link>
                    <div className="mt-2 pt-2 border-t border-line flex justify-end">
                      <button
                        type="button"
                        onClick={() => handleDelete(c)}
                        className="text-xs text-ink-3 hover:text-bad"
                      >
                        删除
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>

      {toast && <Toast kind={toast.kind} message={toast.msg} onClose={() => setToast(null)} />}
    </div>
  );
}
